import React, { useState, useEffect } from "react";
import { auth, db, googleProvider } from "../firebase";
import { signInWithEmailAndPassword, onAuthStateChanged, signInWithPopup } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, addDoc, collection, query, where, getDocs } from "firebase/firestore";
import Logo from "../images/logo.png";
import AlertError from "./PermissionDenied";
import PasswordResetModal from "./PasswordReset";


export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);


  // Check if the user is already logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate("/studio/dashboard");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    setNotice("");
  }, [email, password]);

  useEffect(() => {
    // Check for email parameter in URL
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Load retro font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Share+Tech+Mono:wght@400&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/user-not-found":
        return "No user found with this email.";
      case "auth/wrong-password":
        return "The password is invalid.";
      case "auth/user-disabled":
        return "The user account has been disabled by an administrator.";
      case "auth/account-exists-with-different-credential":
        return "An account already exists with this email. Please sign in with email and password instead.";
      case "auth/email-already-in-use":
        return "An account already exists with this email. Please sign in with email and password instead.";
      case "auth/popup-closed-by-user":
        return "Sign in was cancelled. Please try again.";
      default:
        return "Invalid email or password. Please try again.";
    }
  };

  const handleGoogleLogin = async () => {
    setIsProcessing(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user document exists
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      if (!userDoc.exists()) {
        // Create new user document
        try {
          const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
          const googleFirstName = nameParts[0] || '';
          const googleLastName = nameParts.slice(1).join(' ') || '';

          // Create business document
          const businessRef = await addDoc(collection(db, "company"), {
            ownerId: user.uid,
            name: "",
            createdAt: new Date(),
          });

          // Create user document
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email: user.email,
            firstName: googleFirstName,
            lastName: googleLastName,
            companyId: businessRef.id,
            createdAt: new Date(),
            authProvider: "google",
            emailVerified: true  // Google emails are pre-verified
          });

          // Ensure Firebase Auth user is marked as verified
          if (!auth.currentUser.emailVerified) {
            await auth.currentUser.reload();
          }

          sessionStorage.setItem("Auth Token", result._tokenResponse.refreshToken);
        } catch (dbError) {
          console.error("Database error:", dbError);
          await auth.signOut();
          setNotice("Failed to create account. Please try again or contact support.");
          setIsProcessing(false);
          return;
        }
      } else {
        // User exists, ensure emailVerified is set to true
        const userData = userDoc.data();
        if (!userData.emailVerified) {
          await setDoc(doc(db, "users", user.uid), {
            ...userData,
            emailVerified: true
          }, { merge: true });
        }
        // Ensure Firebase Auth user is marked as verified
        if (!auth.currentUser.emailVerified) {
          await auth.currentUser.reload();
        }
        sessionStorage.setItem("Auth Token", result._tokenResponse.refreshToken);
      }
    } catch (error) {
      console.error("Error signing in with Google: ", error);
      let errorMessage;
      
      switch (error.code) {
        case 'auth/account-exists-with-different-credential':
          // With account linking enabled, this error shouldn't occur
          // but we'll handle it just in case
          errorMessage = "Please try signing in with your email and password first, then link your Google account.";
          if (error.email) {
            setEmail(error.email);
          }
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = "Sign-in cancelled. Please try again.";
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = "Only one popup can be open at a time. Please try again.";
          break;
        case 'auth/popup-blocked':
          errorMessage = "Popup was blocked by your browser. Please enable popups for this site.";
          break;
        case 'auth/email-already-in-use':
          // This might occur if the email is already in use but not yet linked
          errorMessage = "This email is already registered. Please sign in with email and password first.";
          if (error.email) {
            setEmail(error.email);
          }
          break;
        default:
          errorMessage = "An error occurred during sign in. Please try again.";
      }
      
      setNotice(errorMessage);
      // Ensure we're signed out in case of error
      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error("Error signing out:", signOutError);
      }
    }
    setIsProcessing(false);
  };

  const loginWithUsernameAndPassword = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const response = await signInWithEmailAndPassword(auth, email, password);
      sessionStorage.setItem("Auth Token", response._tokenResponse.refreshToken);
    } catch (error) {
      const errorMessage = getErrorMessage(error.code);
      setNotice(errorMessage);
      setIsProcessing(false);
      console.error("Error signing in: ", error);
    }
  };

  const retroStyle = {
    fontFamily: '"Share Tech Mono", monospace',
  };

  return (
    <>
      <style jsx>{`
        .retro-gradient {
          background: linear-gradient(to bottom, #2C3E50, #1A2530);
        }
        .feature-card {
          backdrop-filter: blur(10px);
          border: 1px solid rgba(241, 196, 15, 0.2);
          background: rgba(52, 73, 94, 0.3);
        }
        .floating-icon {
          animation: float 6s ease-in-out infinite;
        }
        .hero-title {
          text-shadow: 2px 2px 0px #E67E22, 4px 4px 0px #D35400, 2px 2px 10px rgba(241, 196, 15, 0.5);
          -webkit-text-stroke: 1px #E67E22;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .retro-button {
          box-shadow: 0 6px 0 #C0392B, 0 8px 15px rgba(231, 76, 60, 0.3);
          transform: translateY(0);
          transition: all 0.1s ease;
        }
        .retro-button:hover {
          transform: translateY(2px);
          box-shadow: 0 4px 0 #C0392B, 0 6px 12px rgba(231, 76, 60, 0.3);
        }
        .retro-button:active {
          transform: translateY(6px);
          box-shadow: 0 0px 0 #C0392B, 0 2px 8px rgba(231, 76, 60, 0.3);
        }
        .game-element {
          animation: pulse 2s ease-in-out infinite alternate;
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      `}</style>
      
      <main className="min-h-screen retro-gradient" style={retroStyle}>
        {/* Clean background effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {/* Simple animated stars */}
          <div className="absolute top-10 left-10 w-1 h-1 bg-yellow-400 animate-pulse"></div>
          <div className="absolute top-20 right-20 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-1/4 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute bottom-20 right-10 w-1 h-1 bg-yellow-400 animate-pulse" style={{animationDelay: '1.5s'}}></div>
          
          {/* Simple gaming elements */}
          <div className="absolute top-16 right-12 text-2xl animate-pulse game-element" style={{animationDelay: '1s'}}>🎮</div>
          <div className="absolute bottom-40 right-20 text-xl animate-pulse game-element" style={{animationDelay: '1.5s'}}>🎲</div>
          <div className="absolute top-1/3 left-8 text-lg animate-bounce game-element" style={{animationDelay: '2.5s', animationDuration: '2.5s'}}>✨</div>
          <div className="absolute bottom-32 left-12 text-xl animate-pulse game-element" style={{animationDelay: '0.8s'}}>🚀</div>
          <div className="absolute top-40 right-1/3 text-purple-400 text-lg animate-bounce game-element" style={{animationDelay: '1.8s', animationDuration: '2.8s'}}>⚡</div>
        </div>

        <div className="relative flex min-h-screen">
          {/* Full Width - Form */}
          <div className="w-full flex flex-col relative z-10">
            {/* Header */}
            <div className="flex items-center justify-between p-6 lg:p-8">
              <a href="/" className="flex items-center space-x-2">
                <div className="text-lg font-black tracking-wider" style={{
                  color: '#F1C40F',
                  textShadow: '1px 1px 0px #E67E22, 2px 2px 0px #D35400'
                }}>
                  GROUP CHAT HEROES
                  <span className="ml-2 text-sm px-2 py-1 border-2 border-yellow-400 rounded font-bold" style={{
                    backgroundColor: '#E74C3C',
                    borderColor: '#F1C40F',
                    color: '#FFFFFF',
                    textShadow: '1px 1px 0px #C0392B'
                  }}>STUDIO</span>
                </div>
              </a>
              <div className="text-sm" style={{color: '#ECF0F1'}}>
                Don't have an account?{' '}
                <Link to="/studio/register" className="font-bold hover:text-yellow-300 transition-colors" style={{color: '#F1C40F'}}>
                  Sign up
                </Link>
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-8">
              <div className="w-full max-w-md mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl lg:text-4xl font-black mb-2 hero-title tracking-wider" style={{color: '#F1C40F'}}>
                    WELCOME BACK
                  </h1>
                  <h1 className="text-4xl lg:text-5xl font-black mb-6 hero-title tracking-wider" style={{color: '#F1C40F'}}>
                    HERO
                  </h1>
                  <p className="text-sm font-bold mb-6" style={{color: '#ECF0F1'}}>
                    Sign in to access your Studio dashboard
                  </p>
                </div>

                {/* Error Message */}
                {notice && (
                  <div className="mb-6">
                    <AlertError text={notice} dashboardButton={false} />
                  </div>
                )}

                                 {/* Sign-in Options */}
                 {!showEmailForm ? (
                   <div className="space-y-4">
                                         {/* Google Sign-in Button */}
                     <button
                       onClick={handleGoogleLogin}
                       disabled={isProcessing}
                       className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white text-gray-700 rounded-lg font-bold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-all duration-200 disabled:opacity-50 shadow-lg"
                     >
                       {isProcessing ? (
                         <div className="flex items-center">
                           <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700 mr-3"></div>
                           LOADING...
                         </div>
                       ) : (
                         <>
                           <svg className="h-5 w-5" viewBox="0 0 24 24">
                             <path
                               fill="#4285F4"
                               d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                             />
                             <path
                               fill="#34A853"
                               d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                             />
                             <path
                               fill="#FBBC05"
                               d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                             />
                             <path
                               fill="#EA4335"
                               d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                             />
                           </svg>
                           CONTINUE WITH GOOGLE
                         </>
                       )}
                     </button>

                                         {/* Divider */}
                     <div className="relative">
                       <div className="absolute inset-0 flex items-center">
                         <div className="w-full border-t-2" style={{borderColor: '#34495E'}}></div>
                       </div>
                       <div className="relative flex justify-center text-sm">
                         <span className="px-2 font-bold" style={{backgroundColor: '#2C3E50', color: '#ECF0F1'}}>OR</span>
                       </div>
                     </div>

                                         {/* Email Sign-in Button */}
                     <button
                       onClick={() => setShowEmailForm(true)}
                       className="w-full flex items-center justify-center px-6 py-3 rounded-lg font-bold transition-all duration-200 retro-button"
                       style={{
                         backgroundColor: '#E74C3C',
                         color: 'white'
                       }}
                     >
                       CONTINUE WITH EMAIL
                     </button>
                  </div>
                ) : (
                  /* Email Form */
                  <form onSubmit={loginWithUsernameAndPassword} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-bold mb-2" style={{color: '#F1C40F'}}>
                        EMAIL ADDRESS
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-lg border-2 py-3 px-4 font-bold transition-all duration-200"
                        style={{
                          backgroundColor: '#ECF0F1',
                          borderColor: '#BDC3C7',
                          color: '#2C3E50'
                        }}
                        placeholder="Enter your email"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label htmlFor="password" className="block text-sm font-bold" style={{color: '#F1C40F'}}>
                          PASSWORD
                        </label>
                        <button
                          type="button"
                          className="text-sm font-bold hover:text-yellow-300 transition-colors"
                          style={{color: '#F1C40F'}}
                          onClick={() => setShowResetModal(true)}
                        >
                          FORGOT PASSWORD?
                        </button>
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-lg border-2 py-3 px-4 font-bold transition-all duration-200"
                        style={{
                          backgroundColor: '#ECF0F1',
                          borderColor: '#BDC3C7',
                          color: '#2C3E50'
                        }}
                        placeholder="Enter your password"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="w-full font-bold text-white rounded-lg px-6 py-4 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed retro-button"
                      style={{
                        backgroundColor: '#E74C3C'
                      }}
                    >
                      {isProcessing ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          SIGNING IN...
                        </div>
                      ) : (
                        'SIGN IN'
                      )}
                    </button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowEmailForm(false)}
                        className="text-sm font-bold hover:text-yellow-300 transition-colors"
                        style={{color: '#F1C40F'}}
                      >
                        ← BACK TO GOOGLE SIGN-IN
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>


        </div>
      </main>

      <PasswordResetModal show={showResetModal} onClose={() => setShowResetModal(false)} auth={auth} />
    </>
  );
}